import { setLocale, getCurrentLocale, SUPPORTED_LOCALES, type AppLocale } from '@/i18n';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  setAccessToken,
  onUnauthorized,
  refreshSession,
} from '@/services/api/client';
import { authApi } from '@/services/api/auth.api';
import { twoFactorApi, type LoginOutcome } from '@/services/api/two-factor.api';
import { useRealtimeStore } from './realtime.store';
import type { PublicUser, UserRole } from '@/types/api';

const USER_STORAGE_KEY = 'app-myticket:user';

function readStoredUser(): PublicUser | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PublicUser) : null;
  } catch {
    return null;
  }
}

function writeStoredUser(user: PublicUser | null) {
  if (user) {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(USER_STORAGE_KEY);
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<PublicUser | null>(readStoredUser());
  const accessToken = ref<string | null>(null);
  const bootstrapping = ref(false);

  const isAuthenticated = computed(() => !!user.value && !!accessToken.value);
  const role = computed<UserRole | null>(() => user.value?.role ?? null);

  function hasRole(...roles: UserRole[]): boolean {
    return !!role.value && roles.includes(role.value);
  }

  function setSession(next: { user: PublicUser; accessToken: string }) {
    user.value = next.user;
    accessToken.value = next.accessToken;
    setAccessToken(next.accessToken);
    writeStoredUser(next.user);
    // El idioma de la cuenta manda en cuanto sabemos quien entra, para que la
    // preferencia acompane a la persona de un dispositivo a otro. Lo que haya
    // en este navegador solo decide antes de identificarse.
    applyAccountLocale(next.user.locale);
    // Open the WebSocket once we have a valid access token.
    useRealtimeStore().connect();
  }

  /**
   * Refresca los datos del usuario sin tocar la sesion. Lo usa el selector de
   * idioma: cambia la preferencia, no quien esta identificado.
   */
  function updateUser(next: PublicUser) {
    user.value = next;
    writeStoredUser(next);
  }

  /** Aplica el idioma guardado en la cuenta, si es uno de los que existen. */
  function applyAccountLocale(locale: string | null | undefined) {
    if (!locale) return;
    if (!(SUPPORTED_LOCALES as readonly string[]).includes(locale)) return;
    if (locale === getCurrentLocale()) return;
    setLocale(locale as AppLocale);
  }

  function clearSession() {
    user.value = null;
    accessToken.value = null;
    setAccessToken(null);
    writeStoredUser(null);
    useRealtimeStore().disconnect();
  }

  /**
   * Signs in. Returns either a session or a pending second-factor challenge —
   * the caller decides what to render. No session exists until the code is
   * accepted, so nothing here can leak an authenticated state.
   */
  async function login(email: string, password: string): Promise<LoginOutcome> {
    const result = (await authApi.login(email, password)) as unknown as LoginOutcome;
    if (result.requiresTwoFactor) return result;
    setSession(result);
    return result;
  }

  /** Second step: completes a challenge with a TOTP or a recovery code. */
  async function verifyTwoFactor(
    challengeToken: string,
    code: string,
  ): Promise<LoginOutcome> {
    const result = await twoFactorApi.verify(challengeToken, code);
    if (!result.requiresTwoFactor) setSession(result);
    return result;
  }

  async function logout() {
    try {
      await authApi.logout();
    } catch {
      /* server-side logout failures are non-fatal */
    } finally {
      clearSession();
    }
  }

  /**
   * Attempt to recover an access token using the refresh cookie.
   * Called once on app boot; if refresh fails the user stays logged out.
   */
  async function bootstrap() {
    if (bootstrapping.value) return;
    bootstrapping.value = true;
    try {
      // Goes through the SHARED refresh, so this never races a background
      // poller renewing at the same instant. The refresh token is single-use:
      // two parallel renewals revoke each other and log a valid user out.
      const result = await refreshSession();
      if (result?.accessToken) {
        setSession(result as unknown as { user: PublicUser; accessToken: string });
      } else {
        clearSession();
      }
    } catch {
      clearSession();
    } finally {
      bootstrapping.value = false;
    }
  }

  // Wire the 401 → logout fallback from the axios client
  onUnauthorized(() => {
    clearSession();
  });

  return {
    user,
    accessToken,
    bootstrapping,
    isAuthenticated,
    role,
    hasRole,
    login,
    verifyTwoFactor,
    logout,
    bootstrap,
    setSession,
    clearSession,
    updateUser,
  };
});

/**
 * Decide where to land each role after login.
 */
export function landingForRole(role: UserRole): string {
  switch (role) {
    case 'ADMIN':
      return '/dashboard';
    case 'SUPPORT':
      return '/assigned';
    case 'CUSTOMER':
    default:
      return '/my-tickets';
  }
}
